import { useRef } from "react";
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, Area, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { Download, ImageDown } from "lucide-react";
import { useData } from "@/context/DataContext";
import { Panel } from "@/components/Widgets";
import { applyUnit, fmtU, periodize, unitLabel } from "@/lib/metrics";
import { downloadCsv } from "@/lib/api";

export const makeRows = (h, cols) => {
  const out = [];
  for (let i = h.from; i <= h.to; i += 1) {
    const r = { idx: i };
    Object.entries(cols).forEach(([k, arr]) => { r[k] = arr ? arr[i] : 0; });
    out.push(r);
  }
  return out;
};

const downloadPng = (el, name) => {
  const svg = el?.querySelector(".recharts-wrapper > svg.recharts-surface") || el?.querySelector("svg.recharts-surface");
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  const { width, height } = svg.getBoundingClientRect();
  img.onload = () => {
    const c = document.createElement("canvas");
    c.width = width * 2;
    c.height = height * 2;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#131c2e";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.scale(2, 2);
    ctx.drawImage(img, 0, 0);
    const a = document.createElement("a");
    a.href = c.toDataURL("image/png");
    a.download = `${name}.png`;
    a.click();
  };
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
};

const Tip = ({ active, payload, label, unit, pctKeys }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-700 bg-[#0f172a] px-3 py-2 shadow-xl">
      <div className="mb-1 text-[11px] font-semibold text-slate-300">FY {label} <span className="font-normal text-slate-500">· click for details</span></div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-6 text-[11px]">
          <span className="flex items-center gap-1.5 text-slate-400"><i className="inline-block h-2 w-2 rounded-full" style={{ background: p.color || p.payload?.fill }} />{p.name}</span>
          <span className="num text-slate-100">{pctKeys.includes(p.dataKey) ? `${Number(p.value).toFixed(1)}%` : fmtU(p.value, unit)}</span>
        </div>
      ))}
    </div>
  );
};

const PieView = ({ slices, onSlice, unit }) => (
  <ResponsiveContainer width="100%" height={260}>
    <PieChart>
      <Pie data={slices} dataKey="value" nameKey="name" innerRadius={55} outerRadius={95} paddingAngle={1} onClick={(d) => onSlice(d.payload || d)}>
        {slices.map((s) => <Cell key={s.name} fill={s.color} stroke="#0B0F19" opacity={s.dim ? 0.3 : 1} className="cursor-pointer" />)}
      </Pie>
      <Tooltip formatter={(v, n, p) => [`${fmtU(v, unit === "pct" ? "cr" : unit)} · ${p.payload.share.toFixed(1)}%`, n]} contentStyle={{ background: "#0f172a", border: "1px solid #334155", fontSize: 11 }} />
      <Legend wrapperStyle={{ fontSize: 10 }} />
    </PieChart>
  </ResponsiveContainer>
);

export const ChartCard = ({ id, title: baseTitle, subtitle, metric, build, stacked, balanceKeys = [], height = 260, className = "" }) => {
  const ref = useRef();
  const ctx = useData();
  const fent = ctx.data.model.entities.find((x) => x.code === ctx.filters.entity);
  const title = fent ? baseTitle.replace(/^Group /, `${fent.short} `) : baseTitle;
  const { data, horizon, filters, setFilters, setDetail } = ctx;
  const res = build({ data, h: horizon, entity: filters.entity, prev: ctx.prev });
  const unit = filters.unit;
  const open = (yi, m = metric, entity = filters.entity, lineKey = null) => setDetail({ yi, metric: m, entity, lineKey, span: filters.period === "3yr" ? Math.min(3, horizon.to - yi + 1) : 1 });
  if (res.empty) {
    return <Panel title={title} subtitle={subtitle} className={className} testid={`chart-${id}`}><div className="py-12 text-center text-sm text-slate-500">{res.empty}</div></Panel>;
  }
  const actions = (
    <>
      <span className="text-[10px] uppercase tracking-wider text-slate-500">{unitLabel(unit)}{filters.period === "3yr" ? " · 3-yr" : ""}</span>
      <button title="Download chart" onClick={() => downloadPng(ref.current, id)} className="text-slate-500 hover:text-amber-400" data-testid={`chart-${id}-download-png`}><ImageDown className="h-4 w-4" /></button>
      <button title="Download data" data-testid={`chart-${id}-download-csv`} className="text-slate-500 hover:text-amber-400"
        onClick={() => (res.slices ? downloadCsv(`${id}.csv`, ["Value"], res.slices.map((s) => ({ label: s.name, values: [s.value] })))
          : downloadCsv(`${id}.csv`, res.rows.map((r) => data.model.years[r.idx]), res.series.map((s) => ({ label: s.name, values: res.rows.map((r) => r[s.key]) }))))}>
        <Download className="h-4 w-4" />
      </button>
    </>
  );
  if (res.slices) {
    const onSlice = (s) => {
      if (s.entity) setFilters((f) => ({ ...f, entity: s.entity }));
      if (s.lineKey || s.entity) open(horizon.from, metric, s.entity || filters.entity, s.lineKey || null);
    };
    return <Panel title={title} subtitle={subtitle} actions={actions} className={className} testid={`chart-${id}`}><div ref={ref}><PieView slices={res.slices} onSlice={onSlice} unit={unit} /></div></Panel>;
  }
  const pctKeys = res.series.filter((s) => s.pct).map((s) => s.key);
  const keys = res.series.filter((s) => !s.pct).map((s) => s.key);
  let rows = res.rows.map((r) => ({ ...r, y: data.model.years[r.idx] }));
  rows = periodize(rows, filters.period, balanceKeys);
  const revenue = rows.map((r) => (res.revenue ? (filters.period === "3yr" ? res.revenue.slice(r.idx, r.idx + 3).reduce((a, b) => a + b, 0) : res.revenue[r.idx]) : 0));
  rows = applyUnit(rows, unit, keys, { stacked, revenue: res.revenue ? revenue : null });
  // recharts 3.6 "sign" offset mis-stacks all-positive series (bars overlap, axis under-scaled) - only use it when negatives exist
  const hasNeg = rows.some((r) => keys.some((k) => r[k] < 0));
  const onChart = (e) => {
    const p = e?.activePayload?.[0]?.payload;
    if (p) open(p.idx);
  };
  const onLegend = (o) => {
    const s = res.series.find((x) => x.key === o.dataKey);
    if (s?.entity) setFilters((f) => ({ ...f, entity: s.entity }));
  };
  return (
    <Panel title={title} subtitle={subtitle} actions={actions} className={className} testid={`chart-${id}`}>
      <div ref={ref}>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={rows} onClick={onChart} stackOffset={stacked && hasNeg ? "sign" : undefined} className="cursor-pointer">
            <CartesianGrid vertical={false} />
            <XAxis dataKey="y" tickLine={false} axisLine={false} />
            <YAxis yAxisId="l" tickLine={false} axisLine={false} width={64} tickFormatter={(v) => (unit === "pct" ? `${Math.round(v)}%` : Math.round(v).toLocaleString("en-IN"))} />
            {pctKeys.length > 0 && <YAxis yAxisId="r" orientation="right" tickLine={false} axisLine={false} width={40} tickFormatter={(v) => `${Math.round(v)}%`} />}
            <Tooltip content={<Tip unit={unit} pctKeys={pctKeys} />} />
            <Legend wrapperStyle={{ fontSize: 10, cursor: "pointer" }} onClick={onLegend} />
            <ReferenceLine yAxisId="l" y={0} stroke="#475569" />
            {res.series.map((s) => {
              const common = { key: s.key, dataKey: s.key, name: s.name, yAxisId: s.pct ? "r" : "l" };
              if (s.type === "line") return <Line {...common} stroke={s.color} strokeWidth={2.5} dot={false} strokeDasharray={s.dash} />;
              if (s.type === "area") return <Area {...common} stroke={s.color} fill={s.color} fillOpacity={0.15} strokeWidth={2} stackId={s.stack} />;
              return (
                <Bar {...common} fill={s.color} stackId={stacked ? "s" : s.stack} radius={stacked ? 0 : [3, 3, 0, 0]}
                  onClick={(d, i, ev) => { ev?.stopPropagation?.(); open(d.idx ?? rows[i].idx, s.metric || metric, s.entity || filters.entity, s.lineKey || null); }}>
                  {s.signColor && rows.map((r) => <Cell key={r.y} fill={r[s.key] >= 0 ? "#10B981" : "#F43F5E"} />)}
                </Bar>
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
};
